import { Autocomplete, Box, Button, TextField, Tooltip, Typography } from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import React, { useEffect, useState } from 'react';
import { LocalizeContextProps, Translate, withLocalize } from 'react-localize-redux';

import { SnackbarTypeSeverity } from '../../../constants/types';
import { getServicesInvestigationService } from '../../../services/agenda';
import { addServiceToRequestService, removeServiceFromRequestService } from '../../../services/request';
import ReasonModal from './ReasonModal';
import { canModifyRequestServices, canRemoveServiceItem, requestTerminologyKey } from './requestModification';
import { IRequest, IRequestServiceInvestigation, IServiceInvestigation, ServiceType } from './types';
import { useRequestMutation } from './useRequestMutation';

export interface RemoveServiceActionCoreProps extends LocalizeContextProps {
    request: IRequest;
    permissions: string[];
    loading: boolean;
    requestServiceType?: ServiceType;
    /** Catalogue to pick a replacement from. Empty until the container has loaded it. */
    availableServices?: IServiceInvestigation[];
    onRemove: (idRequestServiceInvestigation: number, reason: string) => void | Promise<boolean>;
    onReplace?: (idRequestServiceInvestigation: number, servicesInvestigationId: number[], reason: string) => void | Promise<boolean>;
}

// Presentational core: the request's items, one per row. When the user may modify services
// each row carries the two row actions — Modifier (swap this line for another service) and
// Supprimer (disabled for the last remaining service; it must be cancelled, not emptied).
// Both open the shared mandatory-reason confirm modal.
//
// SHER-909: the section heading that used to sit here is gone. The panel that now
// contains this section carries the one heading, and a heading per action was exactly
// the clutter the regroup exists to remove.
//
// SHER-1062: the rows are listed whatever the user may do, read-only when modification is not
// allowed (no permission, or a closed request) — the panel shows what was ordered in every
// state, and only the actions are gated. Rows carry no icon, unlike the reporter's mockup:
// services have no image of their own, and a glyph per module would repeat on every row the
// module the user is already in, so it would carry no information.
export const RemoveServiceActionCore: React.FC<RemoveServiceActionCoreProps> = ({ request, permissions, loading, requestServiceType, availableServices = [], onRemove, onReplace, translate }) => {
    const [selectedId, setSelectedId] = useState<number | null>(null);
    const [replacingId, setReplacingId] = useState<number | null>(null);
    const [replacement, setReplacement] = useState<IServiceInvestigation[]>([]);

    const items = request.requestsServiceInvestigation ?? [];

    if (!canModifyRequestServices(request, permissions)) {
        return <ItemRows items={items} />;
    }
    const removable = canRemoveServiceItem(request, permissions);
    const lastItemHint = translate('pages.hospital.services.request.management.last_item_hint') as string;
    const closeReplace = () => { setReplacingId(null); setReplacement([]); };

    return (
        <>
            <ReasonModal
                open={selectedId !== null}
                closeModal={() => setSelectedId(null)}
                onConfirm={(reason) => onRemove(selectedId as number, reason)}
                loading={loading}
                titleId={requestTerminologyKey(requestServiceType, 'remove_title')}
                bodyId={requestTerminologyKey(requestServiceType, 'remove_modal')}
                reasonLabelId="pages.hospital.services.request.remove_service.reason_label"
                reasonTestId="remove-reason"
            />
            {onReplace &&
                <ReasonModal
                    open={replacingId !== null}
                    closeModal={closeReplace}
                    onConfirm={async (reason) => {
                        const succeeded = await onReplace(replacingId as number, replacement.map((service) => service.id), reason) === true;
                        if (succeeded) { closeReplace(); }
                        return succeeded;
                    }}
                    loading={loading}
                    titleId="pages.hospital.services.request.modify_service.title"
                    bodyId="pages.hospital.services.request.modify_service.modal"
                    reasonLabelId="pages.hospital.services.request.modify_service.reason_label"
                    reasonTestId="modify-reason"
                    confirmDisabled={replacement.length === 0}
                >
                    <Autocomplete
                        multiple
                        options={availableServices}
                        getOptionLabel={(option) => option.description}
                        value={replacement}
                        onChange={(_event, newValue) => setReplacement(newValue)}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                variant="outlined"
                                label={translate(requestTerminologyKey(requestServiceType, 'select_label')) as string}
                                inputProps={{ ...params.inputProps, 'data-testid': 'modify-service-select' }}
                            />
                        )}
                    />
                </ReasonModal>
            }
            <ItemRows
                items={items}
                actions={(item) => (
                    <>
                        {onReplace &&
                            <Button
                                color="inherit"
                                startIcon={<EditOutlinedIcon />}
                                sx={{ color: 'text.secondary' }}
                                data-testid={`modify-service-${item.id}`}
                                disabled={loading}
                                onClick={() => setReplacingId(item.id)}
                            >
                                <Translate id="pages.hospital.services.request.management.modify_item" />
                            </Button>
                        }
                        {/* A disabled button gives no reason by itself; the hint says the last
                            line is dropped by cancelling the request, which is the API's rule. */}
                        <Tooltip title={removable ? '' : lastItemHint}>
                            <span aria-label={removable ? undefined : lastItemHint}>
                                <Button
                                    color="error"
                                    startIcon={<DeleteOutlineIcon />}
                                    data-testid={`remove-service-${item.id}`}
                                    disabled={!removable || loading}
                                    onClick={() => setSelectedId(item.id)}
                                >
                                    <Translate id="pages.hospital.services.request.management.remove_item" />
                                </Button>
                            </span>
                        </Tooltip>
                    </>
                )}
            />
        </>
    );
};

interface ItemRowsProps {
    items: IRequestServiceInvestigation[];
    actions?: (item: IRequestServiceInvestigation) => React.ReactNode;
}

// The bordered list itself, shared by the editable and the read-only rendering so the two
// cannot drift apart visually.
const ItemRows: React.FC<ItemRowsProps> = ({ items, actions }) => {
    if (items.length === 0) {
        return (
            <Typography variant="body2" color="text.secondary" data-testid="request-items-empty">
                <Translate id="pages.hospital.services.request.management.no_items" />
            </Typography>
        );
    }
    return (
        <Box sx={{ border: 1, borderColor: 'divider', borderRadius: '4px' }} data-testid="request-items">
            {items.map((item, index) => (
                <Box
                    key={item.id}
                    sx={{
                        display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '16px', px: '16px', py: '10px',
                        borderTop: index === 0 ? 0 : 1, borderColor: 'divider',
                    }}
                >
                    <Typography variant="body1" sx={{ flex: 1, minWidth: 160 }}>{item.serviceInvestigation?.description}</Typography>
                    {actions && <Box sx={{ display: 'flex', gap: '8px' }}>{actions(item)}</Box>}
                </Box>
            ))}
        </Box>
    );
};

export const RemoveServiceActionCoreLocalized = withLocalize(RemoveServiceActionCore);

interface RemoveServiceActionProps {
    request: IRequest;
    uuidInvestigation: string;
    permissions: string[];
    requestServiceType?: ServiceType;
    onChanged: (updated: IRequest) => void;
    setShowSnackbar: (snackbar: { show: boolean; message?: string; severity?: SnackbarTypeSeverity }) => void;
}

// Container: wires the remove and replace calls to the presentational core, and loads
// the catalogue the replacement picker needs.
const RemoveServiceAction: React.FC<RemoveServiceActionProps> = ({ request, uuidInvestigation, permissions, requestServiceType, onChanged, setShowSnackbar }) => {
    const { loading, run } = useRequestMutation(setShowSnackbar, onChanged);
    const [availableServices, setAvailableServices] = useState<IServiceInvestigation[]>([]);

    useEffect(() => {
        // requestServiceType is optional on this component (it always was — the remove path
        // only used it to pick terminology), so there is nothing to fetch a catalogue for
        // when it is absent. The rows then render without Modifier rather than asking the
        // API for "undefined".
        if (!canModifyRequestServices(request, permissions) || requestServiceType === undefined) {
            return;
        }
        getServicesInvestigationService(uuidInvestigation, requestServiceType)
            .then((response) => setAvailableServices(response.servicesInvestigation || []))
            .catch(() => setAvailableServices([]));
    }, [uuidInvestigation, requestServiceType]);

    const handleRemove = (idRequestServiceInvestigation: number, reason: string) =>
        run(() => removeServiceFromRequestService(uuidInvestigation, request.id, idRequestServiceInvestigation, reason), 'pages.hospital.services.request.remove_service.success');

    // SHER-909 — "Modifier" is add-then-remove against the two endpoints that exist; there
    // is no per-item update endpoint (POST …/service, DELETE …/service/:id and POST …/cancel
    // are the whole surface). David's call was to keep this client-only rather than grow the
    // API, with two consequences worth knowing:
    //
    //  - The pair is NOT atomic. Add runs first deliberately: if the second call fails the
    //    request carries both lines, which is visible and deletable, whereas remove-first
    //    would lose the original line outright on the same failure. Adding first also keeps
    //    the swap possible on a single-service request, which remove-first cannot — the API
    //    refuses to empty a request (LAST_SERVICE).
    //  - The replacement is billed at the service's CURRENT price, not the price the
    //    original line was ordered at. A swap is a new order line, because that is what the
    //    add endpoint creates.
    //
    // The same reason is sent to both calls, so the audit log reads as one intent in two rows.
    const handleReplace = (idRequestServiceInvestigation: number, servicesInvestigationId: number[], reason: string) =>
        run(async () => {
            await addServiceToRequestService(uuidInvestigation, request.id, servicesInvestigationId, reason);
            return removeServiceFromRequestService(uuidInvestigation, request.id, idRequestServiceInvestigation, reason);
        }, 'pages.hospital.services.request.modify_service.success');

    return (
        <RemoveServiceActionCoreLocalized
            request={request}
            permissions={permissions}
            loading={loading}
            requestServiceType={requestServiceType}
            availableServices={availableServices}
            onRemove={handleRemove}
            onReplace={requestServiceType === undefined ? undefined : handleReplace}
        />
    );
};

export default RemoveServiceAction;
